import type { AppPayload, AuditWorkspaceDashboard, AuditWorkspaceOrder, FleetProcess, IntegrationHealth, ProductiveSettings } from "./types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    ...init,
  });

  const body = (await response.json().catch(() => null)) as
    | (T & { error?: string })
    | null;

  if (!response.ok) {
    throw new Error(body?.error || "Não foi possível concluir a operação.");
  }

  return body as T;
}

export const api = {
  login(email: string, password: string) {
    return request<AppPayload>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  logout() {
    return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
  },

  app() {
    return request<AppPayload>("/api/app");
  },

  syncSheet() {
    return request<{ sync: AppPayload["sync"] }>("/api/sync/sheet", { method: "POST" });
  },

  syncAudit() {
    return request<{ sync: AppPayload["auditSync"] }>("/api/sync/audit", { method: "POST" });
  },

  updateProcess(id: number, fields: Partial<FleetProcess>) {
    return request<{ process: FleetProcess }>(`/api/processes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    });
  },

  action(id: number, action: string, payload: Record<string, unknown> = {}) {
    return request<{ process: FleetProcess }>(`/api/processes/${id}/action`, {
      method: "POST",
      body: JSON.stringify({ action, payload }),
    });
  },

  createProcess(payload: Record<string, unknown>) {
    return request<{ process: FleetProcess }>("/api/processes", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  integrationHealth() {
    return request<{ checks: IntegrationHealth[] }>("/api/integrations/health");
  },

  updateProductiveSettings(settings: ProductiveSettings) {
    return request<{ settings: ProductiveSettings }>("/api/settings/productive", {
      method: "PATCH",
      body: JSON.stringify(settings),
    });
  },

  updateAuditAccess(userId: number, canView: boolean) {
    return request<{ ok: true }>("/api/admin/access/audit-os", {
      method: "PATCH",
      body: JSON.stringify({ userId, canView }),
    });
  },

  auditDashboard(start?: string, end?: string) {
    const query = new URLSearchParams();
    if (start) query.set("start", start);
    if (end) query.set("end", end);
    const suffix = query.size ? `?${query.toString()}` : "";
    return request<{ dashboard: AuditWorkspaceDashboard }>(`/api/audit/dashboard${suffix}`);
  },

  auditOrder(os: string) {
    return request<{ order: AuditWorkspaceOrder }>(`/api/audit/orders/${encodeURIComponent(os)}`);
  },

  auditRecord(id: string) {
    return request<{ audit: import("./types").AuditWorkspaceRecord }>(`/api/audit/records/${encodeURIComponent(id)}`);
  },

  auditAction<T>(action: string, payload: Record<string, unknown>) {
    return request<{ data: T; auditSync: AppPayload["auditSync"] }>(`/api/audit/actions/${action}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};
